// Drahtzahlen eines Topic-Stands in Werte umrechnen (Wert = Drahtzahl ×
// Skala), nach den generierten Metadaten -- auch in verschachtelten Typen
// und in Listen von Ganzzahlen (ein Verlauf). Der Client liefert Drahtzahlen
// (docs/protokoll.md); wer Werte anzeigt, rechnet hier um statt je Seite.
//
// Ohne Runen und ohne Laufzeit-Importe, damit node-Tests sie laden
// (lib/protokoll-werte.test.mjs); die Metadaten kommen als Argument,
// `skaliere(familie, …)` mit den generierten steht in skaliere.ts.

import type { FeldInfo } from "./schema.gen";

type Daten = Record<string, unknown>;
export type Typen = Readonly<Record<string, readonly FeldInfo[]>>;

function stellen(skala: number): number {
  if (!(skala > 0) || skala >= 1) return 0;
  return Math.min(9, Math.ceil(-Math.log10(skala) - 1e-9));
}

/** Eine Drahtzahl × Skala, auf die Stellen der Skala gerundet (123 × 0,1 =
 *  12,3 und nicht 12.3000000000000007). */
export function skaliereZahl(draht: number, skala: number): number {
  const w = Number((draht * skala).toFixed(stellen(skala)));
  return Object.is(w, -0) ? 0 : w;
}

function basisTyp(typ: string): string {
  return typ.startsWith("[") && typ.endsWith("]") ? typ.slice(1, -1) : typ;
}

function feldWert(f: FeldInfo, typen: Typen, v: unknown): unknown {
  if (v == null) return v;
  const unter = typen[basisTyp(f.typ)];
  if (Array.isArray(v)) return v.map((e) => elementWert(f, typen, unter, e));
  return elementWert(f, typen, unter, v);
}

function elementWert(f: FeldInfo, typen: Typen, unter: readonly FeldInfo[] | undefined, v: unknown): unknown {
  if (unter && v && typeof v === "object" && !Array.isArray(v)) return skaliereFelder(unter, typen, v as Daten);
  if (typeof v === "number" && f.skala && f.skala > 0) return skaliereZahl(v, f.skala);
  return v;
}

/** skaliereFelder rechnet die Felder eines Stands nach ihren Metadaten um
 *  (`typen`: die verschachtelten Typen, generiert als TYPEN). Unbekannte
 *  Felder bleiben, wie sie sind; `null` bleibt `null`. */
export function skaliereFelder<T = Daten>(felder: readonly FeldInfo[], typen: Typen, daten: Daten): T {
  const out: Daten = { ...daten };
  for (const f of felder) {
    if (f.name in out) out[f.name] = feldWert(f, typen, out[f.name]);
  }
  return out as T;
}

/** Unix-Sekunden (Typ `zeit`) bzw. -Millisekunden (`zeit_ms`) → ISO-Text,
 *  wie die REST-Antworten ihn tragen; fehlt/null → undefined. */
export function isoAusSekunden(s: unknown): string | undefined {
  return typeof s === "number" && Number.isFinite(s) ? new Date(s * 1000).toISOString() : undefined;
}

export function isoAusMs(ms: unknown): string | undefined {
  return typeof ms === "number" && Number.isFinite(ms) ? new Date(ms).toISOString() : undefined;
}
